import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  Copy,
  Eye,
  FileText,
  Globe,
  LayoutTemplate,
  Loader2,
  Lock,
  Mail,
  Monitor,
  Pencil,
  Plus,
  Save,
  Search,
  Smartphone,
  Star,
  Trash2,
  Upload,
  Users,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { emailTemplatesApi } from '../../../api/emailTemplatesApi';
import { subscribersApi } from '../../../api/subscribersApi';
import { useToast } from '../../../contexts/ToastContext';
import { useUiLocale } from '../../../contexts/UiLocaleContext';

/* ═══════════════════ Types ═══════════════════ */

type ToastApi = {
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
};

type ApiAccent = 'neutral' | 'amber' | 'cyan' | 'rose';
type TemplateTab = 'all' | 'system' | 'custom' | 'shared';

interface EmailTemplate {
  id: number;
  name: string;
  description: string;
  tag: string;
  accentColor: string;
  isDefault: boolean;
  isSystem: boolean;
  previewLines: string[];
}

interface SubscriberAssignment {
  id: number;
  email: string;
  username: string;
  templateId: number | null;
  saved: boolean;
}

/* ═══════════════════ Constants ═══════════════════ */

const ACCENT_MAP: Record<string, { border: string; bg: string; text: string; badge: string; dot: string }> = {
  neutral: { border: 'border-blue-400',   bg: 'bg-blue-50',   text: 'text-blue-900',   badge: 'bg-blue-100 text-blue-700',   dot: 'bg-blue-400' },
  amber:   { border: 'border-amber-400',  bg: 'bg-amber-50',  text: 'text-amber-900',  badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-400' },
  cyan:    { border: 'border-cyan-400',   bg: 'bg-cyan-50',   text: 'text-cyan-900',   badge: 'bg-cyan-100 text-cyan-700',   dot: 'bg-cyan-400' },
  rose:    { border: 'border-rose-400',   bg: 'bg-rose-50',   text: 'text-rose-900',   badge: 'bg-rose-100 text-rose-700',   dot: 'bg-rose-400' },
  blue:    { border: 'border-blue-400',   bg: 'bg-blue-50',   text: 'text-blue-900',   badge: 'bg-blue-100 text-blue-700',   dot: 'bg-blue-400' },
  purple:  { border: 'border-purple-400', bg: 'bg-purple-50', text: 'text-purple-900', badge: 'bg-purple-100 text-purple-700', dot: 'bg-purple-400' },
  green:   { border: 'border-green-400',  bg: 'bg-green-50',  text: 'text-green-900',  badge: 'bg-green-100 text-green-700', dot: 'bg-green-400' },
  orange:  { border: 'border-orange-400', bg: 'bg-orange-50', text: 'text-orange-900', badge: 'bg-orange-100 text-orange-700', dot: 'bg-orange-400' },
};

const ACCENT_OPTIONS: { value: ApiAccent; cls: string; labelKey: string }[] = [
  { value: 'neutral', cls: 'bg-blue-500',  labelKey: 'emailTemplates.accent.blue' },
  { value: 'amber',   cls: 'bg-amber-500', labelKey: 'emailTemplates.accent.amber' },
  { value: 'cyan',    cls: 'bg-cyan-500',  labelKey: 'emailTemplates.accent.cyan' },
  { value: 'rose',    cls: 'bg-rose-500',  labelKey: 'emailTemplates.accent.rose' },
];

const TEMPLATE_TAB_KEYS: Record<TemplateTab, string> = {
  all: 'emailTemplates.tab.all',
  system: 'emailTemplates.tab.system',
  custom: 'emailTemplates.tab.custom',
  shared: 'emailTemplates.tab.shared',
};

const GLASS_FRAME =
  'border border-white/50 bg-white/45 shadow-[0_8px_32px_rgba(15,23,42,0.08)] backdrop-blur-xl backdrop-saturate-150';

/* ═══════════════════ Helpers ═══════════════════ */

function isAbortError(e: unknown): boolean {
  const x = e as { name?: string; code?: string };
  return x?.name === 'AbortError' || x?.name === 'CanceledError' || x?.code === 'ERR_CANCELED';
}

function templatesApiError(error: unknown): string {
  const ax = error as { response?: { data?: { detail?: unknown } }; message?: string };
  const detail = ax.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((x: { msg?: string }) => x?.msg).filter(Boolean).join(', ') || 'Request failed';
  return ax.message || 'Request failed';
}

function normalizeApiAccent(value: string | null | undefined): ApiAccent {
  switch (String(value ?? '').toLowerCase()) {
    case 'amber': case 'orange': return 'amber';
    case 'cyan': case 'blue': case 'purple': return 'cyan';
    case 'rose': return 'rose';
    default: return 'neutral';
  }
}

function apiToLocal(t: Record<string, unknown>): EmailTemplate {
  const name = String(t.name ?? '');
  return {
    id: t.id as number,
    name,
    description: String(t.description ?? t.short_description ?? 'Custom email template.'),
    tag: String(t.tag ?? 'custom'),
    accentColor: normalizeApiAccent(String(t.accent_color ?? t.accent ?? t.accentColor ?? 'neutral')),
    isDefault: !!(t.is_default ?? t.isDefault),
    isSystem: !!(t.is_system ?? t.isSystem),
    previewLines: [`📧 ${name}`, '— Your personalized digest content', '— Powered by Hawk AI'],
  };
}

/* ═══════════════════ Component ═══════════════════ */

export function EmailTemplatesPage() {
  const { t, localeTag } = useUiLocale();
  const { showSuccess, showError } = useToast() as ToastApi;

  /* ── Core data ── */
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [templatesLoadError, setTemplatesLoadError] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<SubscriberAssignment[]>([]);

  /* ── Template Library filters ── */
  const [tplSearch, setTplSearch] = useState('');
  const [tplTab, setTplTab] = useState<TemplateTab>('all');

  /* ── Preview mode ── */
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');

  /* ── Assignment filters ── */
  const [assignSearch, setAssignSearch] = useState('');
  const [assignTplFilter, setAssignTplFilter] = useState('all');
  const [assignStatusFilter, setAssignStatusFilter] = useState('all');

  /* ── Dialog state ── */
  const [editTarget, setEditTarget] = useState<EmailTemplate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EmailTemplate | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  /* ── Form state ── */
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formTag, setFormTag] = useState('');
  const [formAccent, setFormAccent] = useState<ApiAccent>('neutral');

  /* ── Change assignment dialog ── */
  const [changingSub, setChangingSub] = useState<SubscriberAssignment | null>(null);
  const [changeTemplateId, setChangeTemplateId] = useState<string>('default');

  /* ═══════════ Data Loading ═══════════ */

  const loadAll = useCallback(async (signal?: AbortSignal, showPageLoading = true) => {
    if (showPageLoading) setLoading(true);
    setTemplatesLoadError(null);
    try {
      const [tplRes, subRes] = await Promise.allSettled([
        emailTemplatesApi.list({ signal }),
        subscribersApi.list({ limit: 500, signal }),
      ]);

      if (tplRes.status === 'fulfilled') {
        const raw: unknown[] = Array.isArray(tplRes.value.data) ? tplRes.value.data : [];
        const mapped = raw.map((t) => apiToLocal(t as Record<string, unknown>));
        setTemplates(mapped);
        setSelectedTemplate((prev) => {
          if (mapped.length === 0) return null;
          if (prev && mapped.some((x) => x.id === prev.id)) return mapped.find((x) => x.id === prev.id) ?? mapped[0] ?? null;
          return mapped.find((t) => t.isDefault) ?? mapped[0] ?? null;
        });
      } else {
        setTemplates([]);
        setSelectedTemplate(null);
        if (!isAbortError(tplRes.reason)) {
          const msg = templatesApiError(tplRes.reason);
          setTemplatesLoadError(msg);
          showError(t('emailTemplates.toastLoadTemplates', { msg }), 5000);
        }
      }

      if (subRes.status === 'fulfilled') {
        const subs: unknown[] = Array.isArray(subRes.value.data) ? subRes.value.data : [];
        setAssignments(subs.map((s) => {
          const sub = s as Record<string, unknown>;
          const rawId = sub.preferred_template_id ?? sub.template_id;
          return {
            id: sub.id as number,
            email: String(sub.mail ?? sub.email ?? ''),
            username: String(sub.username ?? sub.name ?? ''),
            templateId: (rawId !== null && rawId !== undefined) ? Number(rawId) : null,
            saved: true,
          };
        }));
      } else {
        setAssignments([]);
        if (!isAbortError(subRes.reason)) showError(t('emailTemplates.toastLoadSubscribers', { msg: templatesApiError(subRes.reason) }), 5000);
      }
    } catch (e) {
      if (!isAbortError(e)) {
        const msg = templatesApiError(e);
        setTemplatesLoadError(msg);
        showError(msg, 5000);
      }
      setTemplates([]);
      setSelectedTemplate(null);
      setAssignments([]);
    } finally {
      if (showPageLoading) setLoading(false);
    }
  }, [showError, t]);

  useEffect(() => {
    const ac = new AbortController();
    loadAll(ac.signal);
    return () => ac.abort();
  }, [loadAll]);

  /* ═══════════ Derived Values ═══════════ */

  const systemTemplates = templates.filter((t) => t.isSystem);
  const customTemplates = templates.filter((t) => !t.isSystem);

  const templateUsageCount = useMemo(() => {
    const counts: Record<number, number> = {};
    assignments.forEach((a) => { if (a.templateId !== null) counts[a.templateId] = (counts[a.templateId] || 0) + 1; });
    return counts;
  }, [assignments]);

  const assignedSubCount = useMemo(
    () => assignments.filter((a) => a.templateId !== null).length,
    [assignments],
  );

  const filteredTemplates = useMemo(() => {
    let list = templates;
    if (tplTab === 'system' || tplTab === 'shared') list = list.filter((t) => t.isSystem);
    else if (tplTab === 'custom') list = list.filter((t) => !t.isSystem);
    const q = tplSearch.trim().toLowerCase();
    if (q) list = list.filter((t) => t.name.toLowerCase().includes(q) || t.description.toLowerCase().includes(q));
    return list;
  }, [templates, tplTab, tplSearch]);

  const filteredAssignments = useMemo(() => {
    let list = assignments;
    const q = assignSearch.trim().toLowerCase();
    if (q) list = list.filter((a) => a.email.toLowerCase().includes(q) || a.username.toLowerCase().includes(q));
    if (assignTplFilter !== 'all') list = list.filter((a) => String(a.templateId) === assignTplFilter);
    if (assignStatusFilter === 'saved') list = list.filter((a) => a.saved);
    else if (assignStatusFilter === 'unsaved') list = list.filter((a) => !a.saved);
    return list;
  }, [assignments, assignSearch, assignTplFilter, assignStatusFilter]);

  const accent = selectedTemplate ? (ACCENT_MAP[selectedTemplate.accentColor] ?? ACCENT_MAP.neutral) : ACCENT_MAP.neutral;

  /* ═══════════ Handlers ═══════════ */

  const openAdd = () => { setFormName(''); setFormDescription(''); setFormTag(''); setFormAccent('neutral'); setAddOpen(true); };
  const openEdit = (t: EmailTemplate) => { setEditTarget(t); setFormName(t.name); setFormDescription(t.description); setFormTag(t.tag); setFormAccent(normalizeApiAccent(t.accentColor)); };

  const handleAdd = async () => {
    if (!formName.trim()) return;
    setSaving(true);
    try {
      const { data } = await emailTemplatesApi.create({ name: formName.trim(), short_description: formDescription.trim() || 'Custom email template.', tag: formTag.trim() || 'custom', accent: formAccent }, {});
      setTemplates((prev) => [...prev, apiToLocal(data as Record<string, unknown>)]);
      setAddOpen(false);
      showSuccess(t('emailTemplates.toastCreated'));
    } catch (e) { showError(templatesApiError(e), 4500); }
    finally { setSaving(false); }
  };

  const handleSaveEdit = async () => {
    if (!editTarget || !formName.trim()) return;
    setSaving(true);
    try {
      await emailTemplatesApi.update(editTarget.id, { name: formName.trim(), short_description: formDescription.trim(), tag: formTag.trim(), accent: formAccent }, {});
      const updated = { ...editTarget, name: formName.trim(), description: formDescription.trim(), tag: formTag.trim(), accentColor: formAccent };
      setTemplates((prev) => prev.map((t) => (t.id === editTarget.id ? updated : t)));
      if (selectedTemplate?.id === editTarget.id) setSelectedTemplate(updated);
      setEditTarget(null);
      showSuccess(t('emailTemplates.toastUpdated'));
    } catch (e) { showError(templatesApiError(e), 4500); }
    finally { setSaving(false); }
  };

  const handleSetDefault = async (id: number) => {
    const prev = templates;
    setTemplates((ts) => ts.map((t) => ({ ...t, isDefault: t.id === id })));
    try {
      await emailTemplatesApi.setDefault(id, {});
      showSuccess(t('emailTemplates.toastDefaultUpdated'));
      await loadAll(undefined, false);
    } catch (e) { setTemplates(prev); showError(templatesApiError(e), 4500); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await emailTemplatesApi.remove(deleteTarget.id, {});
      setTemplates((prev) => prev.filter((t) => t.id !== deleteTarget.id));
      if (selectedTemplate?.id === deleteTarget.id) setSelectedTemplate(templates.find((t) => t.id !== deleteTarget.id) ?? null);
      setDeleteTarget(null);
      showSuccess(t('emailTemplates.toastDeleted'));
    } catch (e) { setDeleteTarget(null); showError(templatesApiError(e), 4500); }
  };

  const handleResetAssignment = async (subId: number) => {
    try {
      await emailTemplatesApi.assignToSubscriber(subId, null);
      setAssignments((prev) => prev.map((a) => (a.id === subId ? { ...a, templateId: null, saved: true } : a)));
      showSuccess(t('emailTemplates.toastResetAssignment'));
    } catch (e) { showError(templatesApiError(e), 4500); }
  };

  const handleChangeAssignment = async () => {
    if (!changingSub) return;
    const newId = changeTemplateId === 'default' ? null : Number(changeTemplateId);
    try {
      await emailTemplatesApi.assignToSubscriber(changingSub.id, newId);
      setAssignments((prev) => prev.map((a) => (a.id === changingSub.id ? { ...a, templateId: newId, saved: true } : a)));
      showSuccess(t('emailTemplates.toastAssignmentUpdated'));
      setChangingSub(null);
    } catch (e) { showError(templatesApiError(e), 4500); }
  };

  /* ═══════════ Render ═══════════ */

  if (loading) {
    return (
      <div className="relative flex min-h-0 flex-1 flex-col items-center justify-center bg-[rgb(224_231_255/0.6)]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="relative flex h-full min-h-0 bg-[rgb(224_231_255/0.6)]">
      <div className="pointer-events-none absolute -left-24 top-20 h-72 w-72 rounded-full bg-blue-400/20 blur-3xl" aria-hidden />
      <div className="pointer-events-none absolute bottom-32 right-0 h-80 w-80 rounded-full bg-indigo-400/15 blur-3xl" aria-hidden />

      <div className="relative z-10 flex flex-1 min-w-0 flex-col overflow-hidden p-2">
        {/* Header */}
        <header className={`shrink-0 rounded-2xl px-6 py-5 ${GLASS_FRAME}`}>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-800 sm:text-3xl hawk-gradient-text">{t('emailTemplates.title')}</h1>
              <p className="text-sm text-slate-500 mt-0.5">{t('emailTemplates.subtitle')}</p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" className="gap-2" onClick={() => showSuccess(t('emailTemplates.toastImportSoon'))}>
                <Upload className="w-4 h-4" /> {t('emailTemplates.importTemplate')}
              </Button>
              <Button className="gap-2 bg-blue-600 hover:bg-blue-700 text-white" onClick={openAdd}>
                <Plus className="w-4 h-4" /> {t('emailTemplates.newCustomTemplate')}
              </Button>
            </div>
          </div>
        </header>

        {/* Scrollable body */}
        <div className="hawk-scrollbar-neon-blue flex-1 overflow-y-auto px-1 py-3 space-y-4">
          {/* Error banner */}
          {templatesLoadError && (
            <div className="flex items-center justify-between rounded-xl border border-red-200 bg-red-50 px-4 py-3">
              <div className="flex items-start gap-2 text-sm text-red-900">
                <AlertCircle className="mt-0.5 w-4 h-4 shrink-0" />
                <div>
                  <p className="font-medium">{t('emailTemplates.loadErrorTitle')}</p>
                  <p className="mt-0.5 text-red-800/90">{templatesLoadError}</p>
                </div>
              </div>
              <Button variant="outline" className="shrink-0 border-red-300 text-red-900 hover:bg-red-100" onClick={() => void loadAll(undefined, true)}>{t('common.retry')}</Button>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4">
            {([
              { icon: FileText, labelKey: 'emailTemplates.statTotal',     value: templates.length,      subKey: 'emailTemplates.statTotalSub',       bg: '#eff6ff', color: '#2563eb' },
              { icon: Globe,    labelKey: 'emailTemplates.statSystem',    value: systemTemplates.length, subKey: 'emailTemplates.statSystemSub',        bg: '#f0fdf4', color: '#16a34a' },
              { icon: Pencil,   labelKey: 'emailTemplates.statCustom',    value: customTemplates.length, subKey: 'emailTemplates.statCustomSub',            bg: '#eff6ff', color: '#2563eb' },
              { icon: Users,    labelKey: 'emailTemplates.statAssigned', value: assignedSubCount,       subKey: 'emailTemplates.statAssignedSub',    bg: '#fef2f2', color: '#dc2626' },
            ] as const).map((card) => {
              const Icon = card.icon;
              return (
                <div key={card.labelKey} className={`flex items-center gap-4 rounded-xl px-5 py-4 ${GLASS_FRAME}`}>
                  <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: card.bg }}>
                    <Icon className="w-6 h-6" style={{ color: card.color }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-base font-medium text-slate-500 truncate">{t(card.labelKey)}</p>
                    <p className="text-2xl font-bold text-slate-900">{card.value}</p>
                    <p className="text-xs text-slate-400 truncate">{t(card.subKey)}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* 3-Column Layout */}
          <div className="grid grid-cols-12 gap-6">
            {/* ── Template Library ── */}
            <div className="col-span-3 space-y-4">
              <div className={`rounded-2xl p-4 space-y-4 ${GLASS_FRAME}`}>
                <h2 className="text-base font-semibold text-slate-900">{t('emailTemplates.libraryTitle')}</h2>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <Input placeholder={t('emailTemplates.searchPlaceholder')} className="pl-9 bg-white h-9 text-sm" value={tplSearch} onChange={(e) => setTplSearch(e.target.value)} />
                </div>
                <div className="flex gap-1">
                  {(['all', 'system', 'custom', 'shared'] as TemplateTab[]).map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setTplTab(tab)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        tplTab === tab ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {t(TEMPLATE_TAB_KEYS[tab])}
                    </button>
                  ))}
                </div>

                <div className="hawk-scrollbar-neon-blue max-h-[420px] space-y-2 overflow-y-auto pr-1">
                  <AnimatePresence initial={false}>
                    {filteredTemplates.map((tpl) => {
                      const a = ACCENT_MAP[tpl.accentColor] ?? ACCENT_MAP.neutral;
                      const isActive = selectedTemplate?.id === tpl.id;
                      const usedBy = templateUsageCount[tpl.id] ?? 0;
                      return (
                        <motion.div
                          key={tpl.id}
                          layout
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, height: 0 }}
                          onClick={() => setSelectedTemplate(tpl)}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            isActive ? `${a.border} border-2 bg-blue-50/50` : 'border-slate-200 hover:border-slate-300 bg-white'
                          }`}
                        >
                          <div className="flex items-center gap-1.5 flex-wrap mb-1">
                            <span className="font-semibold text-sm text-slate-800">{tpl.name}</span>
                          </div>
                          <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
                            {tpl.isDefault && (
                              <span className="inline-flex items-center text-[10px] font-semibold bg-amber-100 text-amber-700 px-1.5 py-0 rounded">{t('emailTemplates.badge.default')}</span>
                            )}
                            {tpl.isSystem && (
                              <span className="inline-flex items-center text-[10px] font-semibold bg-blue-50 text-blue-600 px-1.5 py-0 rounded border border-blue-200">{t('emailTemplates.badge.shared')}</span>
                            )}
                            <Badge className={`text-[10px] border-0 px-1.5 py-0 ${tpl.isSystem ? 'bg-slate-100 text-slate-600' : a.badge}`}>
                              {tpl.isSystem ? t('common.system') : tpl.tag}
                            </Badge>
                          </div>
                          <p className="text-[11px] text-slate-500 line-clamp-2 mb-1.5">{tpl.description}</p>
                          <p className="text-[10px] text-slate-400">
                            {usedBy === 1
                              ? t('emailTemplates.usedByOne', { count: usedBy })
                              : t('emailTemplates.usedByMany', { count: usedBy })}
                          </p>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                  {filteredTemplates.length === 0 && (
                    <div className="py-8 text-center text-sm text-slate-500">
                      <LayoutTemplate className="w-7 h-7 mx-auto mb-2 text-gray-300" />
                      {t('emailTemplates.emptyTemplates')}
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-slate-400 text-center">
                  {t('emailTemplates.showingTemplates', { shown: filteredTemplates.length, total: templates.length })}
                </p>
              </div>
            </div>

            {/* ── Template Details & Preview ── */}
            <div className="col-span-4 space-y-4">
              <div className={`rounded-2xl p-5 space-y-5 ${GLASS_FRAME}`}>
                <h2 className="text-base font-semibold text-slate-900">{t('emailTemplates.detailsTitle')}</h2>
                {selectedTemplate ? (
                  <>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 mb-2">{selectedTemplate.name}</h3>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {selectedTemplate.isDefault && (
                          <span className="inline-flex items-center text-[10px] font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded">{t('emailTemplates.badge.default')}</span>
                        )}
                        {selectedTemplate.isSystem && (
                          <span className="inline-flex items-center text-[10px] font-semibold bg-blue-50 text-blue-600 px-2 py-0.5 rounded border border-blue-200">{t('emailTemplates.badge.shared')}</span>
                        )}
                        <Badge className={`text-[10px] border-0 px-2 py-0.5 ${selectedTemplate.isSystem ? 'bg-slate-100 text-slate-600' : (ACCENT_MAP[selectedTemplate.accentColor] ?? ACCENT_MAP.neutral).badge}`}>
                          {selectedTemplate.isSystem ? t('common.system') : selectedTemplate.tag}
                        </Badge>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-slate-500 text-xs">{t('emailTemplates.usedByLabel')}</span>
                        <p className="text-slate-800 font-medium">
                          {(templateUsageCount[selectedTemplate.id] ?? 0) === 1
                            ? t('emailTemplates.subscriberOne', { count: templateUsageCount[selectedTemplate.id] ?? 0 })
                            : t('emailTemplates.subscriberMany', { count: templateUsageCount[selectedTemplate.id] ?? 0 })}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-500 text-xs">{t('emailTemplates.lastUpdated')}</span>
                        <p className="text-slate-800 font-medium">{new Date().toLocaleDateString(localeTag, { day: '2-digit', month: '2-digit', year: 'numeric' })}</p>
                      </div>
                      <div>
                        <span className="text-slate-500 text-xs">{t('emailTemplates.createdBy')}</span>
                        <p className="text-slate-800 font-medium">{selectedTemplate.isSystem ? t('emailTemplates.createdBySystem') : t('emailTemplates.createdByYou')}</p>
                      </div>
                      <div className="col-span-2">
                        <span className="text-slate-500 text-xs">{t('common.description')}</span>
                        <p className="text-slate-800 text-sm">{selectedTemplate.description}</p>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h4 className="text-sm font-semibold text-slate-900 mb-3">{t('emailTemplates.actionsTitle')}</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {!selectedTemplate.isDefault && (
                          <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => handleSetDefault(selectedTemplate.id)}>
                            <Star className="w-3.5 h-3.5" /> {t('emailTemplates.setAsDefault')}
                          </Button>
                        )}
                        {selectedTemplate.isDefault && (
                          <Button variant="outline" size="sm" className="gap-1.5 text-xs text-green-700 border-green-300" disabled>
                            <CheckCircle2 className="w-3.5 h-3.5" /> {t('emailTemplates.badge.default')}
                          </Button>
                        )}
                        {!selectedTemplate.isSystem && (
                          <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => openEdit(selectedTemplate)}>
                            <Pencil className="w-3.5 h-3.5" /> {t('emailTemplates.dialogEditTitle')}
                          </Button>
                        )}
                        <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => showSuccess(t('emailTemplates.toastDuplicateSoon'))}>
                          <Copy className="w-3.5 h-3.5" /> {t('common.duplicate')}
                        </Button>
                        <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => showSuccess(t('emailTemplates.toastTestEmailSoon'))}>
                          <Mail className="w-3.5 h-3.5" /> {t('emailTemplates.sendTestEmail')}
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <LayoutTemplate className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">{t('emailTemplates.selectToViewDetails')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* ── Email Preview ── */}
            <div className="col-span-5 space-y-4">
              <div className={`rounded-2xl p-5 space-y-4 ${GLASS_FRAME}`}>
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-semibold text-slate-900">{t('emailTemplates.previewTitle')}</h2>
                  <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setPreviewMode('desktop')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
                        previewMode === 'desktop' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <Monitor className="w-3.5 h-3.5" /> {t('emailTemplates.desktop')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPreviewMode('mobile')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
                        previewMode === 'mobile' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <Smartphone className="w-3.5 h-3.5" /> {t('emailTemplates.mobile')}
                    </button>
                  </div>
                </div>

                {selectedTemplate ? (
                  <div className={`mx-auto transition-all duration-300 ${previewMode === 'mobile' ? 'max-w-[320px]' : 'w-full'}`}>
                    <motion.div
                      key={selectedTemplate.id}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`rounded-xl border-2 ${accent.border} overflow-hidden`}
                    >
                      {/* Email header */}
                      <div className="bg-slate-50 px-4 py-2.5 flex items-center justify-between border-b border-slate-200">
                        <span className="text-[10px] font-medium text-slate-500">{t('emailTemplates.fromLabel')}</span>
                        <span className="text-[10px] text-blue-600 hover:underline cursor-pointer">{t('emailTemplates.viewInBrowser')}</span>
                      </div>
                      {/* Email body */}
                      <div className={`${accent.bg} p-5 space-y-4`}>
                        <div className="space-y-1">
                          <h3 className={`text-base font-bold ${accent.text}`}>{selectedTemplate.previewLines[0]}</h3>
                          <p className="text-[11px] text-slate-500">
                            {new Date().toLocaleDateString(localeTag, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                          </p>
                        </div>
                        <Separator />
                        <div className="space-y-2">
                          <p className={`text-xs ${accent.text} leading-relaxed`}>{t('emailTemplates.previewDigestLine')}</p>
                          <p className={`text-xs ${accent.text} leading-relaxed`}>{t('emailTemplates.previewPoweredLine')}</p>
                          <p className="text-xs text-slate-500 italic">{t('emailTemplates.moreArticles')}</p>
                        </div>
                        <Separator />
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-slate-500">{t('emailTemplates.poweredBy')}</span>
                          <div className="flex items-center gap-2 text-slate-400">
                            <span className="text-xs">🔗</span>
                            <span className="text-xs">🐦</span>
                            <span className="text-xs">📷</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <Eye className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">{t('emailTemplates.selectToPreview')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Subscriber Template Assignments ── */}
          <div className={`rounded-2xl overflow-hidden ${GLASS_FRAME}`}>
            <div className="px-6 py-4 border-b border-white/30">
              <h2 className="text-base font-semibold text-slate-900">{t('emailTemplates.assignmentsTitle')}</h2>
              <p className="text-xs text-slate-500 mt-0.5">{t('emailTemplates.assignmentsSubtitle')}</p>
            </div>

            <div className="flex items-end gap-3 flex-wrap px-6 py-3 border-b border-white/30 bg-white/20 backdrop-blur-sm">
              <div className="relative flex-1 min-w-[180px] max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <Input placeholder={t('emailTemplates.searchSubscriber')} className="pl-9 bg-white h-9 text-sm" value={assignSearch} onChange={(e) => setAssignSearch(e.target.value)} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-500">{t('emailTemplates.filterTemplate')}</label>
                <Select value={assignTplFilter} onValueChange={setAssignTplFilter}>
                  <SelectTrigger className="w-[130px] bg-white h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('common.all')}</SelectItem>
                    {templates.map((tpl) => <SelectItem key={tpl.id} value={String(tpl.id)}>{tpl.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-500">{t('emailTemplates.filterStatus')}</label>
                <Select value={assignStatusFilter} onValueChange={setAssignStatusFilter}>
                  <SelectTrigger className="w-[100px] bg-white h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('common.all')}</SelectItem>
                    <SelectItem value="saved">{t('emailTemplates.statusSaved')}</SelectItem>
                    <SelectItem value="unsaved">{t('emailTemplates.statusUnsaved')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="sm" className="gap-1.5 text-xs h-9" onClick={() => showSuccess(t('emailTemplates.toastBulkAssignSoon'))}>
                {t('emailTemplates.bulkAssign')}
              </Button>
            </div>

            <div className="hawk-scrollbar-neon-blue overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left text-xs font-medium text-slate-500 uppercase tracking-wider border-b border-slate-200">
                    <th className="px-6 py-3 w-8"><input type="checkbox" className="rounded border-slate-300" disabled /></th>
                    <th className="px-4 py-3">{t('common.subscriber')}</th>
                    <th className="px-4 py-3">{t('emailTemplates.colAssignedTemplate')}</th>
                    <th className="px-4 py-3">{t('emailTemplates.colTemplateType')}</th>
                    <th className="px-4 py-3">{t('common.status')}</th>
                    <th className="px-4 py-3">{t('emailTemplates.colLastUpdated')}</th>
                    <th className="px-4 py-3">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAssignments.map((a) => {
                    const tpl = templates.find((t) => t.id === a.templateId);
                    const defaultTpl = templates.find((t) => t.isDefault);
                    const displayTpl = tpl ?? defaultTpl;
                    const accentCls = displayTpl ? (ACCENT_MAP[displayTpl.accentColor] ?? ACCENT_MAP.neutral) : ACCENT_MAP.neutral;

                    return (
                      <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                        <td className="px-6 py-3"><input type="checkbox" className="rounded border-slate-300" disabled /></td>
                        <td className="px-4 py-3">
                          <div>
                            <span className="text-slate-800 font-medium">{a.email}</span>
                            {a.username && <span className="text-slate-400 text-xs ml-1">({a.username})</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className={`inline-block w-2 h-2 rounded-full ${accentCls.dot}`} />
                            <span className="text-slate-800">{displayTpl?.name ?? t('common.default')}</span>
                            {tpl ? (
                              tpl.isDefault ? (
                                <span className="text-[10px] font-semibold bg-amber-100 text-amber-700 px-1.5 py-0 rounded">{t('emailTemplates.badge.default')}</span>
                              ) : !tpl.isSystem ? (
                                <span className="text-[10px] font-semibold bg-purple-100 text-purple-700 px-1.5 py-0 rounded">{t('common.custom')}</span>
                              ) : null
                            ) : (
                              <span className="text-[10px] font-semibold bg-amber-100 text-amber-700 px-1.5 py-0 rounded">{t('emailTemplates.badge.default')}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-slate-600 text-xs">{displayTpl?.isSystem ? t('common.system') : t('common.custom')}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                            a.saved ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-amber-50 text-amber-700 border border-amber-200'
                          }`}>
                            {a.saved ? <><CheckCircle2 className="w-3 h-3" /> {t('emailTemplates.statusSaved')}</> : t('emailTemplates.statusUnsaved')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                          {new Date().toLocaleDateString(localeTag, { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => { setChangingSub(a); setChangeTemplateId(a.templateId !== null ? String(a.templateId) : 'default'); }}
                              className="text-xs text-blue-600 hover:text-blue-700 hover:underline font-medium"
                            >
                              {t('emailTemplates.changeTemplate')}
                            </button>
                            <button
                              type="button"
                              onClick={() => void handleResetAssignment(a.id)}
                              className="text-xs text-blue-600 hover:text-blue-700 hover:underline font-medium"
                            >
                              {t('emailTemplates.resetToDefault')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredAssignments.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center text-sm text-slate-500">{t('emailTemplates.emptySubscribers')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {filteredAssignments.length > 0 && (
              <div className="px-6 py-3 border-t border-white/30 bg-white/15 text-xs text-slate-500">
                {t('emailTemplates.showingSubscribers', { shown: filteredAssignments.length, total: assignments.length })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ═══════════ Dialogs ═══════════ */}

      {/* Add Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Plus className="w-4 h-4 text-blue-600" /> {t('emailTemplates.dialogNewTitle')}</DialogTitle></DialogHeader>
          <TemplateForm name={formName} setName={setFormName} description={formDescription} setDescription={setFormDescription} tag={formTag} setTag={setFormTag} accent={formAccent} setAccent={setFormAccent} />
          <Separator />
          <DialogFooter>
            <DialogClose asChild><Button variant="outline" disabled={saving}>{t('common.cancel')}</Button></DialogClose>
            <Button onClick={handleAdd} disabled={saving || !formName.trim()} className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-1" />{t('common.creating')}</> : <><Save className="w-4 h-4 mr-1" />{t('common.create')}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="w-4 h-4 text-blue-600" /> {t('emailTemplates.dialogEditTitle')}</DialogTitle></DialogHeader>
          <TemplateForm name={formName} setName={setFormName} description={formDescription} setDescription={setFormDescription} tag={formTag} setTag={setFormTag} accent={formAccent} setAccent={setFormAccent} />
          <Separator />
          <DialogFooter>
            <DialogClose asChild><Button variant="outline" disabled={saving}>{t('common.cancel')}</Button></DialogClose>
            <Button onClick={handleSaveEdit} disabled={saving || !formName.trim()} className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-1" />{t('common.saving')}</> : <><Save className="w-4 h-4 mr-1" />{t('emailTemplates.saveChanges')}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600"><AlertCircle className="w-4 h-4" /> {t('emailTemplates.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('emailTemplates.deleteDesc', { name: deleteTarget?.name ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <Button onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white">{t('common.delete')}</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Change Assignment Dialog */}
      <Dialog open={!!changingSub} onOpenChange={(open) => !open && setChangingSub(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-sm">{t('emailTemplates.changeAssignmentTitle')}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">{t('emailTemplates.changeAssignmentDesc', { email: changingSub?.email ?? '' })}</p>
          <Select value={changeTemplateId} onValueChange={setChangeTemplateId}>
            <SelectTrigger className="w-full bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="default">{t('emailTemplates.useDefault')}</SelectItem>
              {templates.map((tpl) => (
                <SelectItem key={tpl.id} value={String(tpl.id)}>
                  <span className="flex items-center gap-2">
                    <span className={`inline-block w-2 h-2 rounded-full ${(ACCENT_MAP[tpl.accentColor] ?? ACCENT_MAP.neutral).dot}`} />
                    {tpl.name} {tpl.isDefault && t('emailTemplates.defaultSuffix')}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">{t('common.cancel')}</Button></DialogClose>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => void handleChangeAssignment()}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ═══════════════════ TemplateForm ═══════════════════ */

interface TemplateFormProps {
  name: string; setName: (v: string) => void;
  description: string; setDescription: (v: string) => void;
  tag: string; setTag: (v: string) => void;
  accent: ApiAccent; setAccent: (v: ApiAccent) => void;
}

function TemplateForm({ name, setName, description, setDescription, tag, setTag, accent, setAccent }: TemplateFormProps) {
  const { t } = useUiLocale();
  return (
    <div className="space-y-4 py-2">
      <div className="space-y-1.5">
        <Label htmlFor="tpl-name">{t('emailTemplates.formName')}</Label>
        <Input id="tpl-name" placeholder={t('emailTemplates.formNamePlaceholder')} value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="tpl-description">{t('common.description')} <span className="text-slate-500 font-normal text-xs">{t('common.optional')}</span></Label>
        <Input id="tpl-description" placeholder={t('emailTemplates.formDescriptionPlaceholder')} value={description} onChange={(e) => setDescription(e.target.value)} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="tpl-tag">{t('emailTemplates.formTag')} <span className="text-slate-500 font-normal text-xs">{t('common.optional')}</span></Label>
        <Input id="tpl-tag" placeholder={t('emailTemplates.formTagPlaceholder')} value={tag} onChange={(e) => setTag(e.target.value)} />
      </div>
      <div className="space-y-2">
        <Label>{t('emailTemplates.formAccent')}</Label>
        <div className="flex items-center gap-3">
          {ACCENT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setAccent(opt.value)}
              className={`w-7 h-7 rounded-full ${opt.cls} transition-all ${
                accent === opt.value ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'opacity-60 hover:opacity-90'
              }`}
              title={t(opt.labelKey)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
